import React, { useState } from 'react';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';
import type { Order, OrderItem } from '../../types';

interface RemakeOrderModalProps {
  order: Order;
  onConfirm: (selectedItems: OrderItem[], reason: string) => void;
  onCancel: () => void;
}

const RemakeOrderModal: React.FC<RemakeOrderModalProps> = ({
  order,
  onConfirm,
  onCancel,
}) => {
  const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(new Set());
  const [remakeReason, setRemakeReason] = useState<string>('');
  const reasonKeyboard = useSoftKeyboardField({
    id: 'remake-reason-other',
    type: 'alphanumeric',
    label: 'Remake reason',
    value: remakeReason,
    onChange: setRemakeReason,
  });

  const toggleItem = (itemId: number) => {
    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedItemIds(new Set(order.items.map(item => item.id)));
  };

  const deselectAll = () => {
    setSelectedItemIds(new Set());
  };

  const handleConfirm = () => {
    const selectedItems = order.items.filter(item => selectedItemIds.has(item.id));
    if (selectedItems.length > 0 && remakeReason.trim()) {
      onConfirm(selectedItems, remakeReason.trim());
    }
  };

  const canConfirm = selectedItemIds.size > 0 && remakeReason.trim() !== '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <header className="p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">Remake Order Items</h2>
        </header>
        <main className="p-4 overflow-y-auto flex-1">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Order Details:</p>
            <div className="p-3 bg-gray-50 rounded border">
              <p className="font-semibold">
                {order.orderType === 'dine-in' 
                  ? `Table: ${order.tableName || `Table ${order.tableNumber || order.tableId}`}`
                  : order.orderType === 'takeaway'
                  ? `Takeaway #${order.takeawayNumber}`
                  : `Delivery #${order.deliveryNumber}`}
              </p>
            </div>
          </div>

          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <p className="text-sm font-semibold text-gray-700">Select Items to Remake:</p>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  Select All
                </button>
                <button
                  onClick={deselectAll}
                  className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Deselect All
                </button>
              </div>
            </div>
            <div className="border rounded max-h-60 overflow-y-auto">
              {order.items.map((item, index) => (
                <label
                  key={`${item.id}-${index}`}
                  className={`flex items-center p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                    selectedItemIds.has(item.id) ? 'bg-blue-50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedItemIds.has(item.id)}
                    onChange={() => toggleItem(item.id)}
                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div className="flex-1">
                    <p className="font-semibold">{item.quantity}x {item.name}</p>
                    {item.selectedMeatOption && (
                      <p className="text-xs text-gray-500">Meat: {item.selectedMeatOption}</p>
                    )}
                    {item.notes && (
                      <p className="text-xs text-gray-500">Note: {item.notes}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">${item.price.toFixed(2)} each</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-2">
              Remake Reason: <span className="text-red-500">*</span>
            </p>
            <select
              value={remakeReason}
              onChange={(e) => setRemakeReason(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Select Reason --</option>
              <option value="Dropped">Dropped</option>
              <option value="Wrong Item">Wrong Item</option>
              <option value="Customer Complaint">Customer Complaint</option>
              <option value="Kitchen Error">Kitchen Error</option>
              <option value="Quality Issue">Quality Issue</option>
              <option value="Other">Other</option>
            </select>
            {remakeReason === 'Other' && (
              <input
                type="text"
                value={remakeReason}
                placeholder="Enter custom reason..."
                className="w-full mt-2 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-soft-keyboard-handled
                {...reasonKeyboard}
              />
            )}
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Selected items will be remade and added to this order at no additional charge. 
              Inventory will be deducted for remade items.
            </p>
          </div>
        </main>
        <footer className="p-4 border-t flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={`px-4 py-2 text-white rounded transition-colors ${
              canConfirm
                ? 'bg-blue-500 hover:bg-blue-600'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Remake Selected ({selectedItemIds.size})
          </button>
        </footer>
      </div>
    </div>
  );
};

export default RemakeOrderModal;

